import { useEffect, useRef, useState } from "react";
import { isSunmiDevice, printOnSunmi } from "../../../utils/sunmiPrinter";
import { toast } from "../../../components/toast";
import { amountInWordsTaka } from "../../../utils/numberToWords";
import { salesInvoiceApi } from "../../../api/salesInvoice";
import { posApi } from "../../../api/pos";
import { unwrapResponse } from "../../../api/client";

interface TransactionItem {
  id: string;
  name: string;
  sku: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  vat?: number;
  total: number;
  isBogoFreeItem?: boolean;
}

interface TransactionDetailsModalProps {
  onClose: () => void;
  transaction: {
    id: string;
    time: string;
    date: string;
    amount: string;
    items: number;
    cashier: string;
    customer?: string;
    paymentMethod: string;
    paymentMethods?: Array<{ method: string; amount: number }>;
    subtotal: number;
    tax: number;
    discount: number;
    total: number;
    itemsList: TransactionItem[];
  };
}

export default function TransactionDetailsModal({
  onClose,
  transaction,
}: TransactionDetailsModalProps) {
  const receiptRef = useRef<HTMLDivElement>(null);
  const [binNumber, setBinNumber] = useState<string | null>(null);
  const [mushokNumber, setMushokNumber] = useState<string>("6.3");
  const [returnPolicy, setReturnPolicy] = useState<string | null>(null);

  useEffect(() => {
    salesInvoiceApi
      .getInvoiceSettings()
      .then((res) => {
        const settings = unwrapResponse<any>(res);
        setBinNumber(settings?.binNumber ?? null);
      })
      .catch(() => {});
    posApi
      .getPosSettings()
      .then((res) => {
        const settings = unwrapResponse<any>(res);
        setMushokNumber(settings?.mishukNumber || "6.3");
        setReturnPolicy(
          settings?.showReturnPolicy ? settings?.returnPolicy || null : null,
        );
      })
      .catch(() => {});
  }, []);

  const totalLineDiscount = transaction.itemsList.reduce(
    (sum, item) => sum + Number(item.discount || 0),
    0,
  );
  const totalLineVat = transaction.itemsList.reduce(
    (sum, item) => sum + Number(item.vat || 0),
    0,
  );
  const totalDiscount =
    transaction.discount > 0 ? transaction.discount : totalLineDiscount;
  const effectiveVat = transaction.tax > 0 ? transaction.tax : totalLineVat;

  const counterInfo = JSON.parse(
    localStorage.getItem("counterCheckedInInfo") || "{}",
  );
  const businessDetails = (() => {
    try { return JSON.parse(localStorage.getItem("businessDetails") || "{}"); } catch { return {}; }
  })();
  const bizData = businessDetails?.data ?? businessDetails;
  const logoUrl = bizData?.imagePath ?? null;
  const bizPhone = bizData?.phoneNumber ?? null;
  const bizCurrency = bizData?.currency?.isO_Code ?? "BDT";
  const bizAddress = (() => {
    const a = bizData?.addressDetail;
    if (!a) return "";
    return [a.addressLine1, a.addressLine2, a.city, a.district, a.division, a.country]
      .map((p) => (p && typeof p === "object" ? p.name : p))
      .filter((p) => p && p !== "string" && p !== "null")
      .join(", ");
  })();
  const outlet = (() => {
    try { return JSON.parse(localStorage.getItem("outlet") || "{}"); } catch { return {}; }
  })();

  const outletName = counterInfo?.outletName ?? "";
  const storeAddress = bizAddress || outlet?.address || outlet?.location || "";
  const storePhone =
    bizPhone ??
    outlet?.phone ??
    outlet?.phoneNumber ??
    outlet?.contactNumber ??
    outlet?.mobileNumber ??
    null;
  const paymentMethods = transaction.paymentMethods ?? [
    { method: transaction.paymentMethod, amount: transaction.total },
  ];
  const totalPaid = paymentMethods.reduce((sum, p) => sum + p.amount, 0);
  const change = totalPaid > transaction.total ? totalPaid - transaction.total : 0;

  const orderNo = transaction.id;
  const dateShort = transaction.date;
  const timeStr = transaction.time;
  const cashier = transaction.cashier;
  const customerName = transaction.customer || "Walk In Customer";
  const customerMobile = "-";
  const customerAddress = "-";

  const lineBreakdown = transaction.itemsList.map((item) => ({
    name: item.name,
    code: item.sku,
    price: item.unitPrice,
    qty: item.quantity,
    total: item.total,
    isBogoFreeItem: item.isBogoFreeItem || false,
    lineDiscount: Number(item.discount || 0),
    lineVat: Number(item.vat || 0),
  }));

  const totalQty = transaction.itemsList.reduce(
    (sum, item) => sum + Number(item.quantity || 0),
    0,
  );
  const subtotal = transaction.subtotal;
  const total = transaction.total;

  const fmt = (n: number) =>
    n.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const blk = { color: "#000" };

  const handlePrint = async () => {
    if (isSunmiDevice()) {
      if (!receiptRef.current) return;
      if (!window.SunmiPrinter?.isConnected()) {
        toast.error(
          "Printer not ready. Please check the printer and try again.",
        );
        return;
      }
      try {
        await printOnSunmi(receiptRef.current);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Print failed");
      }
      return;
    }

    const BLK = "color:#000";

    const itemsHtml = lineBreakdown
      .map(
        (item, idx) => `
        <tr>
          <td style="padding:2px 2px 2px 0;vertical-align:top;font-size:10px;${BLK}">${idx + 1}</td>
          <td style="padding:2px 2px;vertical-align:top;${BLK}">
            <div style="font-size:10px;${BLK}">${item.isBogoFreeItem ? "(Promo) " : ""}${item.name}</div>
            ${item.code ? `<div style="font-size:9px;${BLK}">${item.code}</div>` : ""}
          </td>
          <td style="text-align:center;padding:2px 2px;vertical-align:top;font-size:10px;${BLK}">${item.qty.toFixed(3)}</td>
          <td style="text-align:right;white-space:nowrap;padding:2px 2px;vertical-align:top;font-size:10px;${BLK}">${fmt(item.price)}</td>
          <td style="text-align:right;white-space:nowrap;padding:2px 0 2px 2px;vertical-align:top;font-size:10px;${BLK}">${fmt(item.total)}</td>
        </tr>`,
      )
      .join("");

    const paymentInfoHtml = paymentMethods
      .map(
        (pm) =>
          `<tr><td style="padding:1px 0;font-weight:bold;font-size:11px;${BLK}">${pm.method}</td><td style="text-align:right;padding:1px 0;font-weight:bold;font-size:11px;${BLK}">${fmt(pm.amount)}</td></tr>`,
      )
      .join("");

    const effectiveLogo = logoUrl ?? `${window.location.origin}/ghorer-bazar-favicon.jpg`;

    const receiptHtml = `
      <div style="font-family:'Arial','Helvetica Neue',sans-serif;font-size:11px;line-height:1.3;max-width:300px;padding:0 8px;${BLK}">
        <div style="text-align:center;margin:0;padding:0">
          <div style="display:flex;align-items:center;justify-content:center">
            <img src="${effectiveLogo}" alt="Ghorer Bazar" style="max-width:100%;max-height:80px;width:auto;height:auto;object-fit:contain;display:block" />
          </div>
          ${outletName ? `<div style="font-size:11px;font-weight:bold;margin-top:2px;${BLK}">${outletName}</div>` : ""}
          ${storeAddress ? `<div style="font-size:9px;font-weight:bold;${BLK}">${storeAddress}</div>` : ""}
          ${storePhone ? `<div style="font-size:9px;${BLK}">Phone : ${storePhone}</div>` : ""}
        </div>
        <table style="width:100%;border-collapse:collapse;font-size:9px;margin-top:2px;${BLK}">
          <tr>
            <td style="${BLK}">${binNumber ? `BIN : ${binNumber}` : ""}</td>
            <td style="text-align:right;${BLK}">Mushok : ${mushokNumber}</td>
          </tr>
        </table>
        <div style="border-bottom:1px solid #999;margin:2px 0"></div>

        <table style="width:100%;border-collapse:collapse;font-size:10px;${BLK}">
          <tr><td colspan="2" style="font-weight:bold;${BLK}">Invoice No. : ${orderNo}</td></tr>
          <tr>
            <td style="font-weight:bold;${BLK}">Invoice Date : ${dateShort}</td>
            <td style="text-align:right;white-space:nowrap;font-weight:bold;${BLK}">${timeStr}</td>
          </tr>
          <tr><td colspan="2" style="${BLK}">Name : ${customerName}</td></tr>
          <tr><td colspan="2" style="${BLK}">Address : ${customerAddress}</td></tr>
          <tr><td colspan="2" style="${BLK}">Mobile : ${customerMobile}</td></tr>
          <tr><td colspan="2" style="font-weight:bold;${BLK}">Served by : ${cashier}</td></tr>
        </table>
        <div style="border-bottom:1px solid #999;margin:2px 0"></div>

        <table style="width:100%;border-collapse:collapse;table-layout:fixed;${BLK}">
          <colgroup>
            <col style="width:16px"/>
            <col/>
            <col style="width:36px"/>
            <col style="width:52px"/>
            <col style="width:56px"/>
          </colgroup>
          <thead>
            <tr style="border-bottom:1px solid #000">
              <th style="text-align:left;padding:2px 2px 2px 0;font-size:10px;font-weight:bold;${BLK}">SL</th>
              <th style="text-align:left;padding:2px 2px;font-size:10px;font-weight:bold;${BLK}">Description</th>
              <th style="text-align:center;padding:2px 2px;font-size:10px;font-weight:bold;${BLK}">Sqty</th>
              <th style="text-align:right;padding:2px 2px;font-size:10px;font-weight:bold;${BLK}">MRP</th>
              <th style="text-align:right;padding:2px 0 2px 2px;font-size:10px;font-weight:bold;${BLK}">Amount</th>
            </tr>
          </thead>
          <tbody>${itemsHtml}</tbody>
          <tfoot>
            <tr style="border-top:1px solid #000">
              <td colspan="2" style="padding:2px 2px 2px 0;font-size:10px;font-weight:bold;${BLK}">Sub Total :</td>
              <td style="text-align:center;padding:2px 2px;font-size:10px;font-weight:bold;${BLK}">${totalQty.toFixed(2)}</td>
              <td></td>
              <td style="text-align:right;padding:2px 0 2px 2px;font-size:10px;font-weight:bold;${BLK}">${fmt(subtotal)}</td>
            </tr>
          </tfoot>
        </table>
        <div style="border-bottom:1px solid #999;margin:2px 0"></div>

        <table style="width:100%;border-collapse:collapse;font-size:11px;${BLK}">
          <tr><td style="padding:1px 0;padding-left:70px;${BLK}">Discount :</td><td style="text-align:right;padding:1px 0;${BLK}">${fmt(totalDiscount)}</td></tr>
          <tr><td style="padding:1px 0;padding-left:70px;${BLK}">VAT :</td><td style="text-align:right;padding:1px 0;${BLK}">${fmt(effectiveVat)}</td></tr>
          <tr><td style="padding:1px 0;padding-left:70px;font-weight:bold;${BLK}">Net Amount :</td><td style="text-align:right;padding:1px 0;font-weight:bold;${BLK}">${fmt(total)}</td></tr>
          <tr><td style="padding:1px 0;padding-left:70px;${BLK}">Total Payment :</td><td style="text-align:right;padding:1px 0;${BLK}">${fmt(totalPaid)}</td></tr>
          <tr><td style="padding:1px 0;padding-left:70px;${BLK}">Paid Amount :</td><td style="text-align:right;padding:1px 0;${BLK}">${fmt(totalPaid)}</td></tr>
          <tr><td style="padding:1px 0;padding-left:70px;font-weight:bold;${BLK}">Change Amount :</td><td style="text-align:right;padding:1px 0;font-weight:bold;${BLK}">${fmt(change)}</td></tr>
        </table>

        <div style="font-size:10px;margin:2px 0;${BLK}"><b>In words:</b> <i>${amountInWordsTaka(total)}</i></div>

        <div style="display:inline-block;font-size:11px;font-weight:bold;margin:2px 0 1px;padding-bottom:1px;border-bottom:1px solid #000;${BLK}">Payment Info</div>
        <table style="width:100%;border-collapse:collapse;font-size:11px;${BLK}">
          <tr style="border-bottom:1px solid #000">
            <td style="padding:1px 0;${BLK}">DESCRIPTION</td>
            <td style="text-align:right;padding:1px 0;${BLK}">AMOUNT</td>
          </tr>
          ${paymentInfoHtml}
        </table>

        ${returnPolicy ? `<div class="policy" style="font-size:9px;font-weight:bold;line-height:1.3;margin-top:4px;${BLK}">${returnPolicy}</div>` : ""}

        <div style="text-align:center;font-size:10px;margin-top:4px;line-height:1.4;${BLK}">
          <div style="font-weight:bold;${BLK}">Thank you for shopping</div>
          <div style="font-weight:bold;${BLK}">Customer Copy</div>
        </div>
        <div style="text-align:right;font-size:9px;margin-top:4px;${BLK}">Powered by biznify.net</div>
      </div>`;

    const html = `<!DOCTYPE html><html><head>
      <title>Receipt - ${orderNo}</title>
      <style>
        *{margin:0;padding:0;box-sizing:border-box;color:#000}
        html,body{background:#fff;color:#000;-webkit-print-color-adjust:exact;print-color-adjust:exact}
        body{font-family:'Arial','Helvetica Neue',sans-serif;font-size:11px}
        .page{max-width:300px;margin:0 auto;padding:12px}
        .policy ol,.policy ul{padding-left:14px}
        @media print{
          *{color:#000 !important;-webkit-print-color-adjust:exact;print-color-adjust:exact}
          body{margin:0;padding:0}
          .page{padding:4px;max-width:100%}
        }
      </style>
    </head>
    <body>
      <div class="page">${receiptHtml}</div>
      <script>
        window.onload = function() {
          setTimeout(function(){ window.print(); window.close(); }, 500);
        };
      </` + `script>
    </body></html>`;

    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] min-h-[300px] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <i className="ri-file-list-3-line text-xl text-gray-700"></i>
            <h2 className="text-lg font-semibold text-gray-900">
              Transaction Details
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl text-gray-600"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="bg-gradient-to-br from-teal-50 to-teal-100 border border-teal-200 rounded-lg p-6 mb-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-sm text-teal-700 mb-1">Transaction ID</p>
                <p className="text-2xl font-bold text-teal-900">
                  {transaction.id}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-teal-700 mb-1">Total Amount</p>
                <p className="text-3xl font-bold text-teal-900">
                  {transaction.amount}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-teal-700 mb-1">Date &amp; Time</p>
                <p className="text-sm font-semibold text-teal-900">
                  {transaction.date} at {transaction.time}
                </p>
              </div>
              <div>
                <p className="text-xs text-teal-700 mb-1">Customer</p>
                <p className="text-sm font-semibold text-teal-900">
                  {transaction.customer || "Walk-in"}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-bold text-gray-900">
                Items ({transaction.items})
              </h3>
            </div>

            <div className="divide-y divide-gray-200">
              {transaction.itemsList.map((item) => (
                <div
                  key={item.id}
                  className="p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-900 mb-1">
                        {item.isBogoFreeItem ? "(Promo) " : ""}
                        {item.name}
                      </p>
                      {item.sku && item.sku !== "-" && (
                        <p className="text-xs text-gray-500">SKU: {item.sku}</p>
                      )}
                    </div>
                    <div className="text-right ml-4">
                      <p className="text-sm font-bold text-gray-900">
                        {item.total.toFixed(2)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-xs text-gray-600">
                    <div className="flex items-center gap-4">
                      <span>Qty: {item.quantity}</span>
                      <span>@ {item.unitPrice.toFixed(2)}</span>
                      <span className="text-red-600 font-medium">
                        Disc: {Number(item.discount || 0).toFixed(2)}
                      </span>
                      <span className="text-teal-700 font-medium">
                        VAT: {Number(item.vat || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-bold text-gray-900 mb-3">
              Payment Summary
            </h3>

            <div className="space-y-2">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Subtotal:</span>
                <span className="text-sm font-medium text-gray-900">
                  {transaction.subtotal.toFixed(2)}
                </span>
              </div>

              {totalDiscount > 0 && (
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-red-600">Discount:</span>
                  <span className="text-sm font-medium text-red-600">
                    -{totalDiscount.toFixed(2)}
                  </span>
                </div>
              )}

              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">VAT Total:</span>
                <span className="text-sm font-medium text-gray-900">
                  {effectiveVat.toFixed(2)}
                </span>
              </div>

              {paymentMethods.map((p, idx) => (
                <div
                  key={idx}
                  className="flex justify-between items-center py-2 border-b border-gray-100"
                >
                  <span className="text-sm text-gray-600">{p.method}:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {p.amount.toFixed(2)}
                  </span>
                </div>
              ))}

              <div className="flex justify-between items-center pt-3 border-t-2 border-gray-300">
                <span className="text-base font-bold text-gray-900">
                  Total:
                </span>
                <span className="text-xl font-bold text-teal-600">
                  {transaction.total.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 border-t bg-gray-50 flex gap-3">
          <button
            onClick={handlePrint}
            className="flex-1 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
          >
            <i className="ri-printer-line text-base"></i>
            Print Receipt
          </button>
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>

      {/* Hidden receipt for Sunmi thermal printing */}
      <div
        ref={receiptRef}
        style={{
          position: "absolute",
          left: "-9999px",
          top: 0,
          width: "302px",
          backgroundColor: "#ffffff",
          color: "#000",
        }}
      >
        <div
          style={{
            fontFamily: "'Arial', 'Helvetica Neue', sans-serif",
            fontSize: "13px",
            lineHeight: "1.3",
            color: "#000",
            paddingLeft: "8px",
            paddingRight: "8px",
          }}
        >
          {/* Header */}
          <div style={{ textAlign: "center", margin: 0, paddingTop: "4px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src={logoUrl ?? "/ghorer-bazar-favicon.jpg"}
                alt="Ghorer Bazar"
                style={{
                  maxWidth: "100%",
                  maxHeight: "80px",
                  width: "auto",
                  height: "auto",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </div>
            {outletName && (
              <div style={{ fontSize: "13px", fontWeight: "bold", marginTop: "2px", ...blk }}>
                {outletName}
              </div>
            )}
            {storeAddress && (
              <div style={{ fontSize: "11px", fontWeight: "bold", ...blk }}>{storeAddress}</div>
            )}
            {storePhone && (
              <div style={{ fontSize: "11px", ...blk }}>Phone : {storePhone}</div>
            )}
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px", marginTop: "2px", ...blk }}>
            <tbody>
              <tr>
                <td style={{ ...blk }}>{binNumber ? `BIN : ${binNumber}` : ""}</td>
                <td style={{ textAlign: "right", ...blk }}>Mushok : {mushokNumber}</td>
              </tr>
            </tbody>
          </table>
          <div style={{ borderBottom: "1px solid #999", margin: "2px 0" }} />

          {/* Invoice / Customer info */}
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "12px",
              ...blk,
            }}
          >
            <tbody>
              <tr>
                <td colSpan={2} style={{ fontWeight: "bold", ...blk }}>Invoice No. : {orderNo}</td>
              </tr>
              <tr>
                <td style={{ fontWeight: "bold", ...blk }}>Invoice Date : {dateShort}</td>
                <td style={{ textAlign: "right", whiteSpace: "nowrap", fontWeight: "bold", ...blk }}>
                  {timeStr}
                </td>
              </tr>
              <tr>
                <td colSpan={2} style={blk}>Name : {customerName}</td>
              </tr>
              <tr>
                <td colSpan={2} style={blk}>Address : {customerAddress}</td>
              </tr>
              <tr>
                <td colSpan={2} style={blk}>Mobile : {customerMobile}</td>
              </tr>
              <tr>
                <td colSpan={2} style={{ fontWeight: "bold", ...blk }}>Served by : {cashier}</td>
              </tr>
            </tbody>
          </table>
          <div style={{ borderBottom: "1px solid #999", margin: "2px 0" }} />

          {/* Items table */}
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              tableLayout: "fixed",
              ...blk,
            }}
          >
            <colgroup>
              <col style={{ width: "16px" }} />
              <col />
              <col style={{ width: "36px" }} />
              <col style={{ width: "52px" }} />
              <col style={{ width: "56px" }} />
            </colgroup>
            <thead>
              <tr style={{ borderBottom: "1px solid #000" }}>
                {(["SL", "Description", "Sqty", "MRP", "Amount"] as const).map(
                  (h, i) => (
                    <th
                      key={h}
                      style={{
                        textAlign:
                          i === 0 || i === 1
                            ? "left"
                            : i === 2
                              ? "center"
                              : "right",
                        padding: "2px 2px",
                        fontSize: "12px",
                        fontWeight: "bold",
                        ...blk,
                      }}
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {lineBreakdown.map((item, idx) => (
                <tr key={idx}>
                  <td
                    style={{
                      padding: "2px 2px 2px 0",
                      verticalAlign: "top",
                      fontSize: "12px",
                      ...blk,
                    }}
                  >
                    {idx + 1}
                  </td>
                  <td
                    style={{ padding: "2px 2px", verticalAlign: "top", ...blk }}
                  >
                    <div style={{ fontSize: "12px", ...blk }}>
                      {item.isBogoFreeItem ? "(Promo) " : ""}
                      {item.name}
                    </div>
                    {item.code && (
                      <div style={{ fontSize: "11px", ...blk }}>
                        {item.code}
                      </div>
                    )}
                  </td>
                  <td
                    style={{
                      textAlign: "center",
                      padding: "2px 2px",
                      verticalAlign: "top",
                      fontSize: "12px",
                      ...blk,
                    }}
                  >
                    {item.qty.toFixed(3)}
                  </td>
                  <td
                    style={{
                      textAlign: "right",
                      whiteSpace: "nowrap",
                      padding: "2px 2px",
                      verticalAlign: "top",
                      fontSize: "12px",
                      ...blk,
                    }}
                  >
                    {fmt(item.price)}
                  </td>
                  <td
                    style={{
                      textAlign: "right",
                      whiteSpace: "nowrap",
                      padding: "2px 0 2px 2px",
                      verticalAlign: "top",
                      fontSize: "12px",
                      ...blk,
                    }}
                  >
                    {fmt(item.total)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: "1px solid #000" }}>
                <td colSpan={2} style={{ padding: "2px 2px 2px 0", fontSize: "12px", fontWeight: "bold", ...blk }}>
                  Sub Total :
                </td>
                <td style={{ textAlign: "center", padding: "2px 2px", fontSize: "12px", fontWeight: "bold", ...blk }}>
                  {totalQty.toFixed(2)}
                </td>
                <td />
                <td style={{ textAlign: "right", padding: "2px 0 2px 2px", fontSize: "12px", fontWeight: "bold", ...blk }}>
                  {fmt(subtotal)}
                </td>
              </tr>
            </tfoot>
          </table>
          <div style={{ borderBottom: "1px solid #999", margin: "2px 0" }} />

          {/* Totals */}
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "13px",
              ...blk,
            }}
          >
            <tbody>
              <tr>
                <td style={{ padding: "1px 0", paddingLeft: "70px", ...blk }}>Discount :</td>
                <td style={{ textAlign: "right", padding: "1px 0", ...blk }}>
                  {fmt(totalDiscount)}
                </td>
              </tr>
              <tr>
                <td style={{ padding: "1px 0", paddingLeft: "70px", ...blk }}>VAT :</td>
                <td style={{ textAlign: "right", padding: "1px 0", ...blk }}>
                  {fmt(effectiveVat)}
                </td>
              </tr>
              <tr>
                <td style={{ padding: "1px 0", paddingLeft: "70px", fontWeight: "bold", ...blk }}>Net Amount :</td>
                <td style={{ textAlign: "right", padding: "1px 0", fontWeight: "bold", ...blk }}>
                  {fmt(total)}
                </td>
              </tr>
              <tr>
                <td style={{ padding: "1px 0", paddingLeft: "70px", ...blk }}>Total Payment :</td>
                <td style={{ textAlign: "right", padding: "1px 0", ...blk }}>
                  {fmt(totalPaid)}
                </td>
              </tr>
              <tr>
                <td style={{ padding: "1px 0", paddingLeft: "70px", ...blk }}>Paid Amount :</td>
                <td style={{ textAlign: "right", padding: "1px 0", ...blk }}>
                  {fmt(totalPaid)}
                </td>
              </tr>
              <tr>
                <td style={{ padding: "1px 0", paddingLeft: "70px", fontWeight: "bold", ...blk }}>Change Amount :</td>
                <td style={{ textAlign: "right", padding: "1px 0", fontWeight: "bold", ...blk }}>
                  {fmt(change)}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Amount in words */}
          <div style={{ fontSize: "12px", margin: "2px 0", ...blk }}>
            <b>In words:</b> <i>{amountInWordsTaka(total)}</i>
          </div>

          {/* Payment Info */}
          <div style={{ display: "inline-block", fontSize: "13px", fontWeight: "bold", margin: "2px 0 1px", paddingBottom: "1px", borderBottom: "1px solid #000", ...blk }}>
            Payment Info
          </div>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "13px",
              ...blk,
            }}
          >
            <tbody>
              <tr style={{ borderBottom: "1px solid #000" }}>
                <td style={{ padding: "1px 0", ...blk }}>DESCRIPTION</td>
                <td style={{ textAlign: "right", padding: "1px 0", ...blk }}>AMOUNT</td>
              </tr>
              {paymentMethods.map((pm, idx) => (
                <tr key={idx}>
                  <td style={{ padding: "1px 0", fontWeight: "bold", ...blk }}>{pm.method}</td>
                  <td style={{ textAlign: "right", padding: "1px 0", fontWeight: "bold", ...blk }}>
                    {fmt(pm.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Return policy (HTML from POS settings) */}
          {returnPolicy && (
            <div
              className="[&_ol]:list-decimal [&_ol]:pl-[14px] [&_ul]:list-disc [&_ul]:pl-[14px]"
              style={{ fontSize: "11px", fontWeight: "bold", lineHeight: "1.3", marginTop: "4px", ...blk }}
              dangerouslySetInnerHTML={{ __html: returnPolicy }}
            />
          )}

          {/* Footer */}
          <div
            style={{
              textAlign: "center",
              fontSize: "12px",
              marginTop: "4px",
              lineHeight: "1.4",
              ...blk,
            }}
          >
            <div style={{ fontWeight: "bold", ...blk }}>Thank you for shopping</div>
            <div style={{ fontWeight: "bold", ...blk }}>Customer Copy</div>
          </div>
          <div
            style={{
              textAlign: "right",
              fontSize: "10px",
              marginTop: "4px",
              ...blk,
            }}
          >
            Powered by biznify.net
          </div>
        </div>
      </div>
    </div>
  );
}
